import React from "react";
import { useSiteTheme } from "../../context/SiteThemeContext";
import { isPremiumLoungeTheme } from "../../theme/premiumLoungeClasses";
import { amountInWordsInr, formatInr } from "../../utils/financeFormat";

function Kpi({ label, value, tone, hint, reference, showWords }) {
  const cardClass = reference
    ? "analytics-kpi-card analytics-kpi-card--reference"
    : "analytics-kpi-card";
  const words = showWords ? amountInWordsInr(value) : "";
  return (
    <div className={cardClass}>
      <div className="kpi-label">{label}</div>
      <div className={`kpi-value ${tone || ""}`}>{formatInr(value)}</div>
      {words && words !== "Zero" ? <div className="kpi-words text-secondary small fst-italic">{words}</div> : null}
      {hint ? <p className="text-secondary small mb-0 mt-1">{hint}</p> : null}
    </div>
  );
}

function CollapsibleSection({ title, totalRupee, totalTone = "", showWords, defaultOpen = true, children }) {
  const words = showWords ? amountInWordsInr(totalRupee) : "";
  return (
    <details className="analytics-month-panel analytics-subsection-panel mb-3" open={defaultOpen}>
      <summary className="tracking-tight">
        {title}
        <span className={`float-end ${totalTone}`}>{formatInr(totalRupee)}</span>
      </summary>
      <div className="panel-body">
        <div className="analytics-kpi-grid">{children}</div>
        {words && words !== "Zero" ? (
          <p className="text-secondary small fst-italic mb-0 mt-2">{words}</p>
        ) : null}
      </div>
    </details>
  );
}

export default function ProfitMonthExplorer({ months = [] }) {
  const { themeId } = useSiteTheme();
  const lounge = isPremiumLoungeTheme(themeId);
  const sorted = [...months].sort((a, b) => String(b.periodKey).localeCompare(String(a.periodKey)));
  if (!sorted.length) {
    return <p className="text-secondary small">Upload data and hit Recompute workbook.</p>;
  }
  return (
    <div className={`profit-month-explorer${lounge ? " profit-month-explorer--lounge" : ""}`}>
      {sorted.map((row) => {
        const profit = Number(row.profitRupee) || 0;
        const bankOther = Math.max(
          0,
          (Number(row.variableBankExpenseRupee) || 0)
            - (Number(row.hyperRupee) || 0)
            - (Number(row.plasticRupee) || 0)
            - (Number(row.gasRupee) || 0)
            - (Number(row.digitalMediaRupee) || 0)
        );
        const pbr = Number(row.partybossRevenueRupee) || 0;
        return (
          <details key={row.periodKey} className="analytics-month-panel pb-lounge-card" open={sorted.indexOf(row) === 0}>
            <summary className="tracking-tight">
              {row.displayLabel || row.periodKey}
              <span className={`float-end ${profit < 0 ? "text-danger" : "text-success"}`}>
                Net {formatInr(profit)}
              </span>
            </summary>
            <div className="panel-body">
              <CollapsibleSection
                title="Money in"
                totalRupee={row.totalRevenueRupee}
                totalTone="text-success"
                showWords
                defaultOpen
              >
                <Kpi
                  label="Party Boss (reference only)"
                  value={pbr}
                  reference
                  hint="Not added to total below — this amount is already reflected in Petpooja delivery / aggregator channels."
                />
                <Kpi label="Banquet" value={row.banquetRevenueRupee} />
                <Kpi label="Dine-in (Petpooja)" value={row.restaurantDineInRevenueRupee} />
                <Kpi label="Take-out parcel (Petpooja)" value={row.petpoojaTakeoutParcelRupee} />
                <Kpi label="Take-out other (Petpooja)" value={row.petpoojaTakeoutOtherRupee} />
                <Kpi label="Delivery net (Petpooja)" value={row.petpoojaDeliveryNetRupee} tone="text-muted" />
                <Kpi label="Swiggy dine-out (bank)" value={row.bankSwiggyDineoutRupee ?? row.swiggyRevenueRupee} />
                <Kpi label="Zomato dine-out (bank)" value={row.bankZomatoDineoutRupee ?? row.zomatoRevenueRupee} />
                <Kpi label="EasyDiner dine-out (bank)" value={row.bankEasydinerDineoutRupee ?? row.easydinerRevenueRupee} />
                <Kpi label="Cash In" value={row.cashIncomeRupee} tone="text-success" />
                <Kpi label="Cash In — Partyboss" value={row.cashIncomePartybossRupee} />
                <Kpi label="Cash In — Banquet Hall" value={row.cashIncomeBanquetRupee} />
                <Kpi label="Cash In — Restaurant" value={row.cashIncomeRestaurantRupee} />
              </CollapsibleSection>
              <CollapsibleSection
                title="Money out"
                totalRupee={row.totalExpenseRupee}
                totalTone="text-warning"
                showWords
                defaultOpen
              >
                <Kpi label="Rent (contract)" value={row.rentRupee} />
                <Kpi label="Salaries" value={row.salariesRupee} />
                <Kpi label="Electricity" value={row.electricityRupee} />
                <Kpi label="Digital media" value={row.digitalMediaRupee} />
                <Kpi label="Hyper" value={row.hyperRupee} />
                <Kpi label="Disposables" value={row.plasticRupee} />
                <Kpi label="Gas" value={row.gasRupee} />
                <Kpi label="Coal" value={row.coalRupee} />
                <Kpi label="Dairy products" value={row.dairyRupee} />
                <Kpi label="Meat (total)" value={row.meatTotalRupee} />
                <Kpi label="Chicken (bank)" value={row.chickenRupee} />
                <Kpi label="Ice Cream (bank)" value={row.iceCreamRupee} />
                <Kpi label="Mutton (cash)" value={row.muttonCashRupee} />
                <Kpi label="Sea Food (cash)" value={row.seafoodCashRupee} />
                <Kpi label="Monthly cash (all modules)" value={row.monthlyCashTotalRupee} />
                <Kpi label="Bank other" value={bankOther} />
              </CollapsibleSection>
              {row.petpoojaDeliveryGrossRupee != null ? (
                <p className="text-secondary small mb-0">
                  Petpooja delivery gross {formatInr(row.petpoojaDeliveryGrossRupee)} minus bank dine-out{" "}
                  {formatInr(
                    (Number(row.bankSwiggyDineoutRupee) || 0)
                      + (Number(row.bankZomatoDineoutRupee) || 0)
                      + (Number(row.bankEasydinerDineoutRupee) || 0)
                  )}{" "}
                  = delivery net {formatInr(row.petpoojaDeliveryNetRupee)}.
                </p>
              ) : null}
            </div>
          </details>
        );
      })}
    </div>
  );
}
